import { useMemo } from "react";
import { useParams } from "react-router";

import { tivoAppsApi } from "containers/Request/SRPC/apiClient";
import { RELEASE_STAGE_LABELS, YES_NO_UNKNOWN } from "hooks/tivoApps";
import { TiVoDeviceOem } from "hooks/tivoDevices";
import { FieldProps } from "types/form";
import { toFieldValues } from "utils/formik";

import { TiVoDeviceForm } from "./components/TiVoDeviceForm";
import { TEXTS } from "./constants";
import { getOemTabs } from "./utils";

type FormType = Omit<TiVoDeviceOem, "id">;

const fields: FieldProps[] = [
  {
    label: TEXTS.OEM_NAME_LABEL,
    name: "name",
  },
  {
    label: TEXTS.OEM_SLUG_LABEL,
    name: "slug",
  },
  {
    label: TEXTS.OEM_RS_LABEL,
    name: "applicableReleaseStages",
    type: "checkbox",
    required: false,
    values: toFieldValues(RELEASE_STAGE_LABELS),
  },
  {
    label: TEXTS.OEM_APPROVAL_LABEL,
    name: "publishingConfirmationRequired",
    type: "radio",
    values: toFieldValues(YES_NO_UNKNOWN),
    required: false,
  },
];

function getInitialValues(data?: TiVoDeviceOem, platformId?: string): FormType {
  if (data) {
    return {
      name: data.name,
      slug: data.slug,
      platformId: data.platformId,
      applicableReleaseStages: data.applicableReleaseStages,
      publishingConfirmationRequired: data.publishingConfirmationRequired,
    };
  }
  return {
    name: "",
    slug: "",
    platformId: platformId || "",
    applicableReleaseStages: [],
    publishingConfirmationRequired: "unknown",
  };
}

export function TiVoOemEdit() {
  const { platformId, oemId } = useParams();
  const { data, isLoading } = tivoAppsApi.getTiVoOem.useQuery(
    platformId!,
    oemId!,
    {
      queryOptions: {
        enabled: Boolean(oemId) && Boolean(platformId),
      },
    }
  );

  const updateOem = tivoAppsApi.updateTiVoOem.useMutation();
  const oem = useMemo(() => data?.results, [data?.results]);

  async function handleSubmit(values: FormType) {
    updateOem.mutate({
      args: [platformId!, oemId!],
      body: {
        data: {
          type: "TiVoDeviceOem",
          attributes: values,
        },
      },
    });
  }

  return (
    <TiVoDeviceForm<FormType>
      updateData={updateOem}
      fields={fields}
      formData={getInitialValues(data?.results, platformId)}
      header={oem?.name || ""}
      loading={isLoading}
      onSubmit={handleSubmit}
      tabs={getOemTabs(platformId, oemId)}
    />
  );
}
